import {
  NotificationSettings,
  UserTagNotificationSetting
} from 'src/api/deinverband-api';
import { Attributed, AttributedPatch } from './attributes';
import { Id, Patch } from './common';
import { CategoryAccess, Company } from './company';
import { Contact, ContactPatch } from './contact';
import { Attendance } from './occasion';
import { PostNotification } from './post';
import { Participation } from './survey';

export const phone = /^\+?[\d\s()]*$/;
export const genders = ['female', 'male', 'diverse'] as const;

export type Gender = (typeof genders)[number];
export type Genders = { [K in Gender]: string };

export type GroupOrId = { id: Id; name?: string; number?: string };
export type Group = { id: Id; name: string; number?: string };
export type GroupOrAll = GroupOrId | { id: null };
export type GroupAdd = Omit<Group, 'id'>;
export type GroupPatch = Patch<Group, 'id'>;

export type RoleOrId = { id: Id; name?: string };
export type Role = { id: Id; name: string };
export type RoleOrAll = RoleOrId | { id: null };
export type RoleAdd = Omit<Role, 'id'>;
export type RolePatch = Omit<Role, 'id'>;

export type UserAttributeOrId = { id: Id; name?: string };
export type UserAttribute = { id: Id; name: string };
export type UserAttributeOrAll = UserAttributeOrId | { id: null };

// explicit group roles without all (i.e. user data)
export type GroupRole = {
  group: GroupOrId;
  role: RoleOrId;
};

// group roles with all (i.e. for filters)
export type GroupRoleWithAll = {
  group: GroupOrAll;
  role: RoleOrAll;
};

export const genderData: Genders = {
  male: 'Herr',
  female: 'Frau',
  diverse: 'Divers'
};

export type Capability = 'editor' | 'management' | 'management.' | 'settings';

/// Basic information on any user
export type MemberBase = Attributed & {
  id: Id;
  avatar?: string;
  email?: string;
  // advanced data (only for some requests)
  external: boolean;

  /**
   * @deprecated use attributes instead
   */
  groupRoles?: GroupRole[];
  /**
   * @deprecated use attributes instead
   */
  categoryAccess?: CategoryAccess[];
};

export type PersonalMember = MemberBase & {
  type: 'personal';

  gender?: Gender;
  firstName?: string;
  lastName?: string;
  companies?: Company[];
};

export type CompanyMember = MemberBase & {
  type: 'company';

  company?: Company;
};

export type Member = PersonalMember | CompanyMember;

/// Possibly unpopulated member
export type MemberOrId = { id: Id } | Member;

/// User with attendance info
export interface Attendee {
  member: Member;
  attendance: Attendance;
  // conform to default types
  id: Id;
}

/// User with participation info
export interface Participant {
  member: Member;
  participation: Participation;
  // conform to default types
  id: Id;
}

/// User with notification info
export interface Recipient {
  member: Member;
  email?: PostNotification;
  push?: PostNotification;
  // conform to default types
  id: Id;
}

/// Extended information on a user profile as provided by the backend
export type ProfileBase = Attributed & {
  id: Id;
  avatar?: string;
  email?: string;

  // attributes
  tenantAdmin: boolean;
  editor: boolean;
  capabilities: Capability[];

  // advanced data
  external: boolean;
  defaultNotifications: NotificationSettings;
  tagNotifications: UserTagNotificationSetting[];

  /**
   * @deprecated use attributes instead
   */
  groupRoles: GroupRole[];
  /**
   * @deprecated use attributes instead
   */
  categoryAccess: CategoryAccess[];
};

export type PersonalProfile = ProfileBase & {
  type: 'personal';

  gender?: Gender;
  firstName?: string;
  lastName?: string;
  contact?: Contact;
  companies?: Company[];
};

export type CompanyProfile = ProfileBase & {
  type: 'company';

  company?: Company;
};

export type Profile = PersonalProfile | CompanyProfile;

export type PersonalProfileAdd = Omit<
  PersonalProfile,
  | 'id'
  | 'tenantAdmin'
  | 'editor'
  | 'tagNotifications'
  | 'defaultNotifications'
  | 'companies'
> & {
  companyIds: Id[];
  defaultNotifications?: NotificationSettings;
};

export type CompanyProfileAdd = Omit<
  CompanyProfile,
  | 'id'
  | 'tenantAdmin'
  | 'editor'
  | 'tagNotifications'
  | 'defaultNotifications'
  | 'company'
> & {
  companyId: Id;
  defaultNotifications?: NotificationSettings;
};

export type ProfileAdd = PersonalProfileAdd | CompanyProfileAdd;

export type PersonalProfilePatch = Patch<
  AttributedPatch<PersonalProfile>,
  'id',
  'tenantAdmin' | 'editor' | 'tagNotifications' | 'contact' | 'companies'
> & {
  companyIds?: Id[];
  contact?: ContactPatch;
};

export type CompanyProfilePatch = Patch<
  AttributedPatch<CompanyProfile>,
  'id',
  'tenantAdmin' | 'editor' | 'tagNotifications' | 'company'
> & {
  companyId: Id;
};

export type ProfilePatch = PersonalProfilePatch | CompanyProfilePatch;
